/**
 * Executive reporting (B5, ADR-053).
 *
 * Own module per the B3 convention so shared `client.ts` / `types.ts` stay
 * untouched. Wire shapes mirror `server/src/admin/reports.service.ts` and
 * `costs.service.ts` field for field — a rename on either side breaks the
 * other. Amounts are decimal STRINGS end to end; this file never coerces.
 */
import { http } from './http.ts';
import { MOCK_MODE } from './client.ts';
import type { Currency } from './types.ts';

export interface NgrCurrencyLine {
  currency: string;
  turnover: string;
  payouts: string;
  grossGamingRevenue: string;
  bonusCost: string;
  rakebackCost: string;
  affiliateCost: string;
  /** MODELLED — labelled so wherever it is shown (ADR-053). */
  railFeesModelled: string;
  costEntries: string;
  netGamingRevenue: string;
  bonusRatioPct: string | null;
  activePlayers: number;
  arppu: string | null;
}

export interface NgrReport {
  from: string;
  to: string;
  ftdCount: number;
  currencies: NgrCurrencyLine[];
}

export interface ExposureLine {
  currency: string;
  playerBalances: string;
  bonusLocked: string;
  /** Earned, unclaimed rakeback (B7, ADR-066) — owed at the moment of claim. */
  rakebackUnclaimed: string;
  total: string;
}

export interface WithdrawalSla {
  pendingCount: number;
  oldestHours: string | null;
  p50Hours: string | null;
  p95Hours: string | null;
}

export type CostKind = 'psp_fee' | 'chargeback' | 'write_off' | 'true_up' | 'adjustment';

export const COST_KINDS: CostKind[] = ['psp_fee', 'chargeback', 'write_off', 'true_up', 'adjustment'];

export interface CostEntryView {
  id: number;
  kind: string;
  currency: string;
  amount: string;
  note: string;
  refType: string | null;
  refId: number | null;
  operatorId: number;
  createdAt: string;
}

const EMPTY_NGR: NgrReport = { from: '', to: '', ftdCount: 0, currencies: [] };

/** LIVE — `GET /internal/admin/reports/ngr?from&to` (days back from today). */
export async function fetchNgr(days: number): Promise<NgrReport> {
  if (MOCK_MODE) return EMPTY_NGR;
  const to = new Date().toISOString().slice(0, 10);
  const from = new Date(Date.now() - (days - 1) * 86_400_000).toISOString().slice(0, 10);
  return http.get<NgrReport>('/internal/admin/reports/ngr', { from, to });
}

/** LIVE — `GET /internal/admin/reports/exposure`. */
export async function fetchExposure(): Promise<ExposureLine[]> {
  if (MOCK_MODE) return [];
  const body = await http.get<{ currencies: ExposureLine[] }>('/internal/admin/reports/exposure');
  return body.currencies;
}

/** LIVE — `GET /internal/admin/reports/withdrawal-sla`. */
export async function fetchWithdrawalSla(): Promise<WithdrawalSla> {
  if (MOCK_MODE) return { pendingCount: 0, oldestHours: null, p50Hours: null, p95Hours: null };
  return http.get<WithdrawalSla>('/internal/admin/reports/withdrawal-sla');
}

/** LIVE — `GET /internal/admin/costs`. The manual audited cost drawer. */
export async function fetchCostEntries(limit = 50): Promise<CostEntryView[]> {
  if (MOCK_MODE) return [];
  const body = await http.get<{ entries: CostEntryView[] }>('/internal/admin/costs', { limit });
  return body.entries;
}

export interface NewCostEntry {
  kind: CostKind;
  currency: Currency;
  amount: string;
  note: string;
}

/** LIVE — `POST /internal/admin/costs`. Admin only, audited (`cost.record`). */
export async function addCostEntry(entry: NewCostEntry): Promise<CostEntryView> {
  return http.post<CostEntryView>('/internal/admin/costs', entry);
}
